// src/components/onboarding/onboarding-stepper.tsx
import React, { useEffect, useRef } from "react";
import { Check, Lock, Loader2 } from "lucide-react";
import type { StageDefinition } from "@/api/onboarding-api";

interface OnboardingStepperProps {
  stages: StageDefinition[];
  currentStage: number;
  customerStatus: string;
  runningStage: number | null;
  onStageClick: (n: number) => void;
  /** Highest stage the user is allowed to click, independent of currentStage's
   *  visuals. Defaults to currentStage. Used so a re-measuring customer (or one
   *  whose backend current_stage briefly regressed mid-pipeline) can always
   *  navigate back to a stage they've actually reached, never getting trapped. */
  maxNavigableStage?: number;
}

/**
 * N-stage horizontal stepper.
 *
 * Fully dynamic — it iterates the `stages` array, so it renders correctly for
 * any stage count (10, 11, …). No functional change was needed when Stage 10
 * "AI brand monitoring" was added and the run-history stage moved to Stage 11;
 * this component already adapts to `stages.length` automatically.
 *
 * Layout strategy:
 *   - Each stage = a numbered/checked circle (28px) with a 2-line label below.
 *   - Stages are connected by a 1-px line that sits at the center-y of the
 *     circles. The line gets the "completed" colour up to currentStage.
 *   - Container has equal-width flex children on desktop (all stages fit at
 *     ~1280px+), and falls back to horizontal scroll-snap on narrower
 *     viewports (phones, narrow tablets). With 11 stages the per-stage
 *     min-width keeps each readable while scrolling on smaller screens.
 *   - Active stage is auto-scrolled into view whenever currentStage changes,
 *     so the user never has to swipe to find where they are.
 *
 * State per stage:
 *   - completed (n < currentStage)           → teal filled circle + check
 *   - active    (n === currentStage)         → teal outlined circle, bolded
 *   - running   (n === runningStage)         → spinner replaces the number
 *   - locked    (n > currentStage)           → muted circle + Lock icon
 *   - completed-customer overrides locked    → status === "completed" makes
 *                                              ALL stages clickable.
 */
export function OnboardingStepper({
  stages,
  currentStage,
  customerStatus,
  runningStage,
  onStageClick,
  maxNavigableStage,
}: OnboardingStepperProps) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef<HTMLButtonElement | null>(null);
  const isCompleted = customerStatus === "completed";
  const navCeiling = Math.max(maxNavigableStage ?? currentStage, currentStage);

  // Auto-scroll the active step into view when it changes. Only horizontally,
  // and "nearest" so we don't yank the page if the active stage is already
  // on-screen.
  useEffect(() => {
    if (!activeRef.current || !scrollerRef.current) return;
    const el = activeRef.current;
    const container = scrollerRef.current;

    // Compute element's offset within the scroll container and ease it in.
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const offset =
      el.offsetLeft - container.offsetLeft - (containerRect.width - elRect.width) / 2;
    container.scrollTo({ left: Math.max(0, offset), behavior: "smooth" });
  }, [currentStage, runningStage]);

  return (
    <div
      className="rounded-xl"
      style={{
        backgroundColor: "var(--card)",
        border: "1px solid var(--border)",
        padding: "16px 8px 12px",
      }}
    >
      <div
        ref={scrollerRef}
        className="flex w-full overflow-x-auto"
        style={{
          scrollSnapType: "x proximity",
          // Hide scrollbar visually but keep it tabbable
          scrollbarWidth: "thin",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {stages.map((stage, idx) => {
          const n = stage.number;
          const isLast = idx === stages.length - 1;

          // Status
          const completed = isCompleted || n < currentStage;
          const active = !isCompleted && n === currentStage;
          const running = runningStage === n;
          const locked = !isCompleted && n > currentStage;

          // Clickability — gated on the (possibly broadened) nav ceiling, not
          // just currentStage, so the user is never trapped on a later stage.
          const clickable = isCompleted || n <= navCeiling;

          // Visual roles
          const circleBg = completed
            ? "var(--alpha-teal)"
            : active
            ? "var(--background)"
            : "var(--muted)";
          const circleBorder = completed
            ? "var(--alpha-teal)"
            : active
            ? "var(--alpha-teal)"
            : "var(--border)";
          const circleFg = completed
            ? "white"
            : active
            ? "var(--alpha-teal)"
            : "var(--muted-foreground)";

          // Connector colour: teal between every pair of completed stages,
          // and between the last completed and the active one.
          const connectorCompleted = isCompleted || n < currentStage;
          const connectorColor = connectorCompleted
            ? "var(--alpha-teal)"
            : "var(--border)";

          return (
            <div
              key={n}
              className="relative flex flex-col items-center"
              style={{
                // Equal width per stage on desktop so all stages fit.
                // min-width keeps each readable on mobile while scrolling
                // (matters more now that there are 11 stages).
                flex: "1 1 0",
                minWidth: 92,
                paddingLeft: 4,
                paddingRight: 4,
                scrollSnapAlign: "center",
              }}
            >
              {/* Connector line — sits behind the circle, between this
                  stage and the NEXT one. Skipped on the last stage. */}
              {!isLast && (
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    top: 14,
                    left: "calc(50% + 14px)",
                    right: "calc(-50% + 14px)",
                    height: 2,
                    backgroundColor: connectorColor,
                    zIndex: 0,
                  }}
                />
              )}

              {/* Circle button */}
              <button
                ref={active ? activeRef : null}
                onClick={() => clickable && onStageClick(n)}
                disabled={!clickable}
                title={`Stage ${n} — ${stage.title}`}
                aria-current={active ? "step" : undefined}
                aria-label={`Stage ${n}: ${stage.title}${
                  completed
                    ? " (completed)"
                    : active
                    ? " (current)"
                    : " (locked)"
                }`}
                style={{
                  position: "relative",
                  zIndex: 1,
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: circleBg,
                  border: `2px solid ${circleBorder}`,
                  color: circleFg,
                  cursor: clickable ? "pointer" : "not-allowed",
                  transition: "all 0.15s ease",
                  flexShrink: 0,
                  // Active glow
                  boxShadow: active
                    ? "0 0 0 4px var(--alpha-teal-10)"
                    : "none",
                }}
                onMouseOver={(e) => {
                  if (!clickable) return;
                  e.currentTarget.style.transform = "scale(1.08)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                {running ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : completed ? (
                  <Check className="w-3.5 h-3.5" strokeWidth={3} />
                ) : locked ? (
                  <Lock className="w-3 h-3" />
                ) : (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {n}
                  </span>
                )}
              </button>

              {/* Label */}
              <div
                style={{
                  marginTop: 8,
                  textAlign: "center",
                  maxWidth: 110,
                }}
              >
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: active
                      ? "var(--alpha-teal)"
                      : "var(--muted-foreground)",
                    lineHeight: 1.2,
                    marginBottom: 2,
                  }}
                >
                  Stage {n}
                </div>
                <div
                  className="line-clamp-2"
                  style={{
                    fontSize: 11,
                    fontWeight: active || completed ? 600 : 500,
                    color: active
                      ? "var(--foreground)"
                      : completed
                      ? "var(--foreground)"
                      : "var(--muted-foreground)",
                    lineHeight: 1.25,
                    // Robust 2-line clamp without relying on the plugin
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    wordBreak: "break-word",
                  }}
                >
                  {stage.title}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}